import {
  ContractReportQueryDto,
  ContractReportOptionParameter,
  ContractReportSortColumn,
  SortDirection,
  useGetProjectsByIdsQuery,
  useListContractReportOptionsQuery,
} from "../../../autogen/bff-api";

interface UseContractReportOptionsParams {
  debouncedFilters: {
    q: ContractReportQueryDto;
    sortBy?: ContractReportSortColumn;
    sortDirection?: SortDirection;
    sortByCustomField?: string;
  };
  openDropdown: string | null;
  optionsSearchTerms?: Partial<ContractReportQueryDto>;
  optionsPages?: number;
}

const OPTIONS_PAGE_SIZE = 50;

import { base64Encode } from "../../../common/utils/base64";

const encodeQueryWithPropNullified = ({
  q,
  prop,
  limit,
  skip = 0,
}: {
  q: ContractReportQueryDto;
  prop: keyof ContractReportQueryDto;
  limit?: number;
  skip?: number;
}): string => base64Encode(JSON.stringify({ ...q, skip, limit, [prop]: null }));

export const useContractReportOptions = (params: UseContractReportOptionsParams) => {
  const { debouncedFilters, openDropdown, optionsSearchTerms = {}, optionsPages = 1 } = params;
  const optionsLimit = optionsPages * OPTIONS_PAGE_SIZE;
  const optionListQuery = (prop: keyof ContractReportQueryDto) =>
    encodeQueryWithPropNullified({ q: debouncedFilters.q, prop, limit: optionsLimit });

  const isOpen = (key: string): boolean => {
    if (!openDropdown) return false;
    return openDropdown === `${key}-filter`;
  };

  const {
    data: projectOptions,
    isLoading: projectOptionsLoading,
    isFetching: projectOptionsFetching,
    isError: projectOptionsFailed,
    refetch: refetchProjectOptions,
  } = useListContractReportOptionsQuery(
    {
      ...debouncedFilters,
      q: optionListQuery("projectIds"),
      parameterName: ContractReportOptionParameter.ProjectId,
      optionSearchTerm: optionsSearchTerms.projectSearchTerm ?? undefined,
    },
    { skip: !isOpen("projectDetails"), refetchOnFocus: false }
  );

  const {
    data: selectedProjects,
    isLoading: selectedProjectsLoading,
    isFetching: selectedProjectsFetching,
  } = useGetProjectsByIdsQuery(
    { projectIds: debouncedFilters.q.projectIds ?? [] },
    { skip: !debouncedFilters.q.projectIds?.length, refetchOnFocus: false }
  );

  const {
    data: counterpartyOptions,
    isLoading: counterpartyOptionsLoading,
    isFetching: counterpartyOptionsFetching,
    isError: counterpartyOptionsFailed,
    refetch: refetchCounterpartyOptions,
  } = useListContractReportOptionsQuery(
    {
      ...debouncedFilters,
      q: optionListQuery("counterpartyIds"),
      parameterName: ContractReportOptionParameter.CounterpartyId,
      optionSearchTerm: optionsSearchTerms.counterpartyName ?? undefined,
    },
    { skip: !isOpen("counterparty"), refetchOnFocus: false }
  );

  const {
    data: selectedCounterpartyOptions,
    isLoading: selectedCounterpartyOptionsLoading,
    isFetching: selectedCounterpartyOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      ...debouncedFilters,
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "counterpartyName" }),
      parameterName: ContractReportOptionParameter.CounterpartyId,
      optionValues: debouncedFilters.q.counterpartyIds ?? undefined,
    },
    { skip: !debouncedFilters.q.counterpartyIds?.length, refetchOnFocus: false }
  );

  const {
    data: internalOwnerOptions,
    isLoading: internalOwnerOptionsLoading,
    isFetching: internalOwnerOptionsFetching,
    isError: internalOwnerOptionsFailed,
    refetch: refetchInternalOwnerOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("internalOwnerIds"),
      parameterName: ContractReportOptionParameter.InternalOwnerId,
      optionSearchTerm: optionsSearchTerms.internalOwnerSearchTerm ?? undefined,
    },
    { skip: !isOpen("responsible"), refetchOnFocus: false }
  );

  const {
    data: selectedInternalOwnerOptions,
    isLoading: selectedInternalOwnerOptionsLoading,
    isFetching: selectedInternalOwnerOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "internalOwnerSearchTerm" }),
      parameterName: ContractReportOptionParameter.InternalOwnerId,
      optionValues: debouncedFilters.q.internalOwnerIds ?? undefined,
    },
    { skip: !debouncedFilters.q.internalOwnerIds?.length, refetchOnFocus: false }
  );

  const {
    data: owningOrgOptions,
    isLoading: owningOrgOptionsLoading,
    isFetching: owningOrgOptionsFetching,
    isError: owningOrgOptionsFailed,
    refetch: refetchOwningOrgOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("owningOrganizationIds"),
      parameterName: ContractReportOptionParameter.OwningOrganizationId,
      optionSearchTerm: optionsSearchTerms.owningOrganizationName ?? undefined,
    },
    { skip: !isOpen("owner"), refetchOnFocus: false }
  );

  const {
    data: selectedOwningOrgOptions,
    isLoading: selectedOwningOrgOptionsLoading,
    isFetching: selectedOwningOrgOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "owningOrganizationName" }),
      parameterName: ContractReportOptionParameter.OwningOrganizationId,
      optionValues: debouncedFilters.q.owningOrganizationIds ?? undefined,
    },
    { skip: !debouncedFilters.q.owningOrganizationIds?.length, refetchOnFocus: false }
  );

  const {
    data: templateOptions,
    isLoading: templateOptionsLoading,
    isFetching: templateOptionsFetching,
    isError: templateOptionsFailed,
    refetch: refetchTemplateOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("templateIds"),
      parameterName: ContractReportOptionParameter.TemplateId,
      optionSearchTerm: optionsSearchTerms.templateNameSearchTerm ?? undefined,
    },
    { skip: !isOpen("type"), refetchOnFocus: false }
  );

  const {
    data: selectedTemplateOptions,
    isLoading: selectedTemplateOptionsLoading,
    isFetching: selectedTemplateOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "templateNameSearchTerm" }),
      parameterName: ContractReportOptionParameter.TemplateId,
      optionValues: debouncedFilters.q.templateIds ?? undefined,
    },
    { skip: !debouncedFilters.q.templateIds?.length, refetchOnFocus: false }
  );

  const {
    data: referenceNumberOptions,
    isLoading: referenceNumberOptionsLoading,
    isFetching: referenceNumberOptionsFetching,
    isError: referenceNumberOptionsFailed,
    refetch: refetchReferenceNumberOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("referenceNumbers"),
      parameterName: ContractReportOptionParameter.ReferenceNumber,
      optionSearchTerm: optionsSearchTerms.referenceNumberSearchTerm ?? undefined,
    },
    { skip: !isOpen("referenceNumber"), refetchOnFocus: false }
  );

  const {
    data: selectedReferenceNumberOptions,
    isLoading: selectedReferenceNumberOptionsLoading,
    isFetching: selectedReferenceNumberOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "referenceNumberSearchTerm" }),
      parameterName: ContractReportOptionParameter.ReferenceNumber,
      optionValues: debouncedFilters.q.referenceNumbers ?? undefined,
    },
    { skip: !debouncedFilters.q.referenceNumbers?.length, refetchOnFocus: false }
  );

  const {
    data: internalPartyOptions,
    isLoading: internalPartyOptionsLoading,
    isFetching: internalPartyOptionsFetching,
    isError: internalPartyOptionsFailed,
    refetch: refetchInternalPartyOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("internalPartyIds"),
      parameterName: ContractReportOptionParameter.InternalPartyId,
      optionSearchTerm: optionsSearchTerms.internalPartySearchTerm ?? undefined,
    },
    { skip: !isOpen("internalParties"), refetchOnFocus: false }
  );

  const {
    data: selectedInternalPartyOptions,
    isLoading: selectedInternalPartyOptionsLoading,
    isFetching: selectedInternalPartyOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "internalPartySearchTerm" }),
      parameterName: ContractReportOptionParameter.InternalPartyId,
      optionValues: debouncedFilters.q.internalPartyIds ?? undefined,
    },
    { skip: !debouncedFilters.q.internalPartyIds?.length, refetchOnFocus: false }
  );

  const {
    data: currencyOptions,
    isLoading: currencyOptionsLoading,
    isFetching: currencyOptionsFetching,
    isError: currencyOptionsFailed,
    refetch: refetchCurrencyOptions,
  } = useListContractReportOptionsQuery(
    {
      q: base64Encode(JSON.stringify({ ...debouncedFilters.q, skip: 0, limit: optionsLimit })),
      parameterName: ContractReportOptionParameter.Currency,
    },
    { skip: !isOpen("currency"), refetchOnFocus: false }
  );

  const {
    data: originSourcingEventOptions,
    isLoading: originSourcingEventOptionsLoading,
    isFetching: originSourcingEventOptionsFetching,
    isError: originSourcingEventOptionsFailed,
    refetch: refetchOriginSourcingEventOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("originSourcingEventIds"),
      parameterName: ContractReportOptionParameter.OriginSourcingEventId,
      optionSearchTerm: optionsSearchTerms.originSourcingEventName ?? undefined,
    },
    { skip: !isOpen("originSourcingEvent"), refetchOnFocus: false }
  );

  const {
    data: selectedOriginSourcingEventOptions,
    isLoading: selectedOriginSourcingEventOptionsLoading,
    isFetching: selectedOriginSourcingEventOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "originSourcingEventName" }),
      parameterName: ContractReportOptionParameter.OriginSourcingEventId,
      optionValues: debouncedFilters.q.originSourcingEventIds ?? undefined,
    },
    {
      skip: !debouncedFilters.q.originSourcingEventIds?.length,
      refetchOnFocus: false,
    }
  );

  const {
    data: publishedByOptions,
    isLoading: publishedByOptionsLoading,
    isFetching: publishedByOptionsFetching,
    isError: publishedByOptionsFailed,
    refetch: refetchPublishedByOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("publishedByIds"),
      parameterName: ContractReportOptionParameter.PublishedByName,
      optionSearchTerm: optionsSearchTerms.publishedBySearchTerm ?? undefined,
    },
    { skip: !isOpen("publishedBy"), refetchOnFocus: false }
  );

  const {
    data: selectedPublishedByOptions,
    isLoading: selectedPublishedByOptionsLoading,
    isFetching: selectedPublishedByOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "publishedBySearchTerm" }),
      parameterName: ContractReportOptionParameter.PublishedByName,
      optionValues: debouncedFilters.q.publishedByIds ?? undefined,
    },
    {
      skip: !debouncedFilters.q.publishedByIds?.length,
      refetchOnFocus: false,
    }
  );

  const {
    data: terminatedByOptions,
    isLoading: terminatedByOptionsLoading,
    isFetching: terminatedByOptionsFetching,
    isError: terminatedByOptionsFailed,
    refetch: refetchTerminatedByOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("terminatedByIds"),
      parameterName: ContractReportOptionParameter.TerminatedById,
      optionSearchTerm: optionsSearchTerms.terminatedBySearchTerm ?? undefined,
    },
    { skip: !isOpen("terminatedBy"), refetchOnFocus: false }
  );

  const {
    data: selectedTerminatedByOptions,
    isLoading: selectedTerminatedByOptionsLoading,
    isFetching: selectedTerminatedByOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "terminatedBySearchTerm" }),
      parameterName: ContractReportOptionParameter.TerminatedById,
      optionValues: debouncedFilters.q.terminatedByIds ?? undefined,
    },
    { skip: !debouncedFilters.q.terminatedByIds?.length, refetchOnFocus: false }
  );

  const {
    data: counterpartyContactNameOptions,
    isLoading: counterpartyContactNameOptionsLoading,
    isFetching: counterpartyContactNameOptionsFetching,
    isError: counterpartyContactNameOptionsFailed,
    refetch: refetchCounterpartyContactNameOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("counterpartyContactFullNames"),
      parameterName: ContractReportOptionParameter.CounterpartyContactFullName,
      optionSearchTerm: optionsSearchTerms.counterpartyContactFullName ?? undefined,
    },
    { skip: !isOpen("counterpartyContactFullName"), refetchOnFocus: false }
  );

  const {
    data: selectedCounterpartyContactNameOptions,
    isLoading: selectedCounterpartyContactNameOptionsLoading,
    isFetching: selectedCounterpartyContactNameOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "counterpartyContactFullName" }),
      parameterName: ContractReportOptionParameter.CounterpartyContactFullName,
      optionValues: debouncedFilters.q.counterpartyContactFullNames ?? undefined,
    },
    {
      skip: !debouncedFilters.q.counterpartyContactFullNames?.length,
      refetchOnFocus: false,
    }
  );

  const {
    data: counterpartyContactEmailOptions,
    isLoading: counterpartyContactEmailOptionsLoading,
    isFetching: counterpartyContactEmailOptionsFetching,
    isError: counterpartyContactEmailOptionsFailed,
    refetch: refetchCounterpartyContactEmailOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("counterpartyContactEmails"),
      parameterName: ContractReportOptionParameter.CounterpartyContactEmail,
      optionSearchTerm: optionsSearchTerms.counterpartyContactEmail ?? undefined,
    },
    { skip: !isOpen("counterpartyContactEmail"), refetchOnFocus: false }
  );

  const {
    data: selectedCounterpartyContactEmailOptions,
    isLoading: selectedCounterpartyContactEmailOptionsLoading,
    isFetching: selectedCounterpartyContactEmailOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "counterpartyContactEmail" }),
      parameterName: ContractReportOptionParameter.CounterpartyContactEmail,
      optionValues: debouncedFilters.q.counterpartyContactEmails ?? undefined,
    },
    {
      skip: !debouncedFilters.q.counterpartyContactEmails?.length,
      refetchOnFocus: false,
    }
  );

  const {
    data: counterpartyContactPhoneOptions,
    isLoading: counterpartyContactPhoneOptionsLoading,
    isFetching: counterpartyContactPhoneOptionsFetching,
    isError: counterpartyContactPhoneOptionsFailed,
    refetch: refetchCounterpartyContactPhoneOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("counterpartyContactPhoneNumbers"),
      parameterName: ContractReportOptionParameter.CounterpartyContactPhoneNumber,
      optionSearchTerm: optionsSearchTerms.counterpartyContactPhoneNumber ?? undefined,
    },
    { skip: !isOpen("counterpartyContactPhone"), refetchOnFocus: false }
  );

  const {
    data: selectedCounterpartyContactPhoneOptions,
    isLoading: selectedCounterpartyContactPhoneOptionsLoading,
    isFetching: selectedCounterpartyContactPhoneOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "counterpartyContactPhoneNumber" }),
      parameterName: ContractReportOptionParameter.CounterpartyContactPhoneNumber,
      optionValues: debouncedFilters.q.counterpartyContactPhoneNumbers ?? undefined,
    },
    {
      skip: !debouncedFilters.q.counterpartyContactPhoneNumbers?.length,
      refetchOnFocus: false,
    }
  );

  const {
    data: linkedContractOptions,
    isLoading: linkedContractOptionsLoading,
    isFetching: linkedContractOptionsFetching,
    isError: linkedContractOptionsFailed,
    refetch: refetchLinkedContractOptions,
  } = useListContractReportOptionsQuery(
    {
      q: optionListQuery("linkedContractIds"),
      parameterName: ContractReportOptionParameter.LinkedContractId,
      optionSearchTerm: optionsSearchTerms.linkedContractTitleSearchTerm ?? undefined,
    },
    { skip: !isOpen("linkedContracts"), refetchOnFocus: false }
  );

  const {
    data: selectedLinkedContractOptions,
    isLoading: selectedLinkedContractOptionsLoading,
    isFetching: selectedLinkedContractOptionsFetching,
  } = useListContractReportOptionsQuery(
    {
      q: encodeQueryWithPropNullified({ q: debouncedFilters.q, prop: "linkedContractTitleSearchTerm" }),
      parameterName: ContractReportOptionParameter.LinkedContractId,
      optionValues: debouncedFilters.q.linkedContractIds ?? undefined,
    },
    { skip: !debouncedFilters.q.linkedContractIds?.length, refetchOnFocus: false }
  );

  return {
    projectOptions,
    projectOptionsLoading,
    projectOptionsFetching,
    projectOptionsFailed,
    refetchProjectOptions,
    selectedProjects,
    selectedProjectsLoading,
    selectedProjectsFetching,
    counterpartyOptions,
    counterpartyOptionsLoading,
    counterpartyOptionsFetching,
    counterpartyOptionsFailed,
    refetchCounterpartyOptions,
    selectedCounterpartyOptions,
    selectedCounterpartyOptionsLoading,
    selectedCounterpartyOptionsFetching,
    internalOwnerOptions,
    internalOwnerOptionsLoading,
    internalOwnerOptionsFetching,
    internalOwnerOptionsFailed,
    refetchInternalOwnerOptions,
    selectedInternalOwnerOptions,
    selectedInternalOwnerOptionsLoading,
    selectedInternalOwnerOptionsFetching,
    owningOrgOptions,
    owningOrgOptionsLoading,
    owningOrgOptionsFetching,
    owningOrgOptionsFailed,
    refetchOwningOrgOptions,
    selectedOwningOrgOptions,
    selectedOwningOrgOptionsLoading,
    selectedOwningOrgOptionsFetching,
    templateOptions,
    templateOptionsLoading,
    templateOptionsFetching,
    templateOptionsFailed,
    refetchTemplateOptions,
    selectedTemplateOptions,
    selectedTemplateOptionsLoading,
    selectedTemplateOptionsFetching,
    referenceNumberOptions,
    referenceNumberOptionsLoading,
    referenceNumberOptionsFetching,
    referenceNumberOptionsFailed,
    refetchReferenceNumberOptions,
    selectedReferenceNumberOptions,
    selectedReferenceNumberOptionsLoading,
    selectedReferenceNumberOptionsFetching,
    internalPartyOptions,
    internalPartyOptionsLoading,
    internalPartyOptionsFetching,
    internalPartyOptionsFailed,
    refetchInternalPartyOptions,
    selectedInternalPartyOptions,
    selectedInternalPartyOptionsLoading,
    selectedInternalPartyOptionsFetching,
    currencyOptions,
    currencyOptionsLoading,
    currencyOptionsFetching,
    currencyOptionsFailed,
    refetchCurrencyOptions,
    originSourcingEventOptions,
    originSourcingEventOptionsLoading,
    originSourcingEventOptionsFetching,
    originSourcingEventOptionsFailed,
    refetchOriginSourcingEventOptions,
    selectedOriginSourcingEventOptions,
    selectedOriginSourcingEventOptionsLoading,
    selectedOriginSourcingEventOptionsFetching,
    publishedByOptions,
    publishedByOptionsLoading,
    publishedByOptionsFetching,
    publishedByOptionsFailed,
    refetchPublishedByOptions,
    selectedPublishedByOptions,
    selectedPublishedByOptionsLoading,
    selectedPublishedByOptionsFetching,
    terminatedByOptions,
    terminatedByOptionsLoading,
    terminatedByOptionsFetching,
    terminatedByOptionsFailed,
    refetchTerminatedByOptions,
    selectedTerminatedByOptions,
    selectedTerminatedByOptionsLoading,
    selectedTerminatedByOptionsFetching,
    counterpartyContactNameOptions,
    counterpartyContactNameOptionsLoading,
    counterpartyContactNameOptionsFetching,
    counterpartyContactNameOptionsFailed,
    refetchCounterpartyContactNameOptions,
    selectedCounterpartyContactNameOptions,
    selectedCounterpartyContactNameOptionsLoading,
    selectedCounterpartyContactNameOptionsFetching,
    counterpartyContactEmailOptions,
    counterpartyContactEmailOptionsLoading,
    counterpartyContactEmailOptionsFetching,
    counterpartyContactEmailOptionsFailed,
    refetchCounterpartyContactEmailOptions,
    selectedCounterpartyContactEmailOptions,
    selectedCounterpartyContactEmailOptionsLoading,
    selectedCounterpartyContactEmailOptionsFetching,
    counterpartyContactPhoneOptions,
    counterpartyContactPhoneOptionsLoading,
    counterpartyContactPhoneOptionsFetching,
    counterpartyContactPhoneOptionsFailed,
    refetchCounterpartyContactPhoneOptions,
    selectedCounterpartyContactPhoneOptions,
    selectedCounterpartyContactPhoneOptionsLoading,
    selectedCounterpartyContactPhoneOptionsFetching,
    linkedContractOptions,
    linkedContractOptionsLoading,
    linkedContractOptionsFetching,
    linkedContractOptionsFailed,
    refetchLinkedContractOptions,
    selectedLinkedContractOptions,
    selectedLinkedContractOptionsLoading,
    selectedLinkedContractOptionsFetching,
  };
};
